/**
 * «Crear alerta gratis» with nothing picked yet (Spain, `features.alertLanding`):
 * the reader asked for the onboarding search step — «¿Qué ofreces?» — by hand.
 *
 * The onboarding sheet (`onboarding-modal.tsx`) opens by itself at most once a
 * day and three times in all (`onboarding-gate.ts`), which is right for an
 * unasked prompt and wrong for a button the reader just pressed. This is the
 * one way past that gate:
 *
 *  - a SIGNED-IN reader: `requestOnboardingSearch()` fires
 *    {@link ONBOARDING_REQUEST_EVENT}, and the mounted sheet opens on the
 *    search step at once, even if it was dismissed earlier in the visit;
 *  - a GUEST: the flag is set before the button sends him to /register, and
 *    the sheet takes it ({@link takeOnboardingRequest}) once he is signed in
 *    and back on the catalogue — a client-side navigation, so the in-memory
 *    flag survives it; `sessionStorage` carries it over a reload as well.
 *
 * Nothing else sets it, so on UZ and KZ the sheet behaves exactly as before.
 */
export const ONBOARDING_REQUEST_EVENT = 'tenders:onboarding-search';

const KEY = 'uzt_onboarding_request';

let pending = false;

function session(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Ask the onboarding sheet for its search step, now or after sign-in. */
export function requestOnboardingSearch(): void {
  pending = true;
  try {
    session()?.setItem(KEY, '1');
  } catch {
    /* private mode — the in-memory flag still carries a client navigation */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ONBOARDING_REQUEST_EVENT));
  }
}

/** Is a request waiting? Clears it — the sheet opens for it once. */
export function takeOnboardingRequest(): boolean {
  let stored = false;
  try {
    const s = session();
    stored = s?.getItem(KEY) === '1';
    s?.removeItem(KEY);
  } catch {
    /* unreadable storage reads as no stored request */
  }
  const had = pending || stored;
  pending = false;
  return had;
}
